import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { colors } from 'syngenta-digital-cropwise-react-ui-kit';
import { ModalAnimationDirections } from 'base/types/ModalBox';

import {
  StyledCard,
  StyledTitle,
  StyledParagraph,
  StyledIcon,
  StyledTextContainer,
  StyledLink,
  StyledLinkCookieSettings,
  StyledHr,
} from './HelpContactPopup.styles';
import IconHelp from 'components/Icons/IconHelp';
import { useMemo, useState } from 'react';
import { useBreakpoint } from 'hooks';
import {
  SouthAfricaCountryConstant,
  HungaryCountryConstant,
  NetherlandsCountryConstant,
} from 'utils/constants/CountryData';

interface IProps {
  handleCloseIconClick: () => void;
  email: string;
  phone?: string;
  countryCode?: string;
}

const HelpContactPopup = ({
  handleCloseIconClick,
  email,
  phone,
  countryCode,
}: IProps): JSX.Element => {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();
  const [slidingDirection, setSlidingDirection] = useState<ModalAnimationDirections | undefined>(
    isMobile ? ModalAnimationDirections.Up : undefined
  );

  const NLBookAnAppointmentLink = 'https://www.syngenta.nl/cropwise-seed-selector-afspraak';
  const NLContactEmail = 'Zaden.Advies@syngenta.com';

  const handleCloseButtonClick = () => {
    if (isMobile) {
      setSlidingDirection(ModalAnimationDirections.Down);
    } else {
      handleCloseIconClick();
    }
  };

  const handleAnimationEnd = () => {
    if (!isMobile || slidingDirection === ModalAnimationDirections.Up) {
      return;
    }
    handleCloseIconClick();
  };
  const showContactDetails = useMemo(() => {
    if (
      countryCode === SouthAfricaCountryConstant.id ||
      countryCode === SouthAfricaCountryConstant.label
    ) {
      return (
        <StyledParagraph>
          {t('Please reach out to our area sales managers for help or queries at')}&nbsp;
          <StyledLink href={`${email.startsWith('http') ? '' : 'mailto:'}${email}`} target="_blank">
            {email}
          </StyledLink>
        </StyledParagraph>
      );
    }
    if (
      countryCode === NetherlandsCountryConstant.id ||
      countryCode === NetherlandsCountryConstant.label
    ) {
      return (
        <StyledParagraph>
          {t('Book an appointment with our Cropwise Expert')}&nbsp;
          <StyledLink
            href={NLBookAnAppointmentLink}
            target="_blank"
            className="contact-detail-span"
          >
            {t('via the calendar')}
          </StyledLink>
          &nbsp;{t('or send us an email at')}&nbsp;
          <StyledLink
            href={`${NLContactEmail.startsWith('http') ? '' : 'mailto:'}${NLContactEmail}`}
          >
            {NLContactEmail}
          </StyledLink>
          &nbsp;
          {`${t('Thank you')}.`}
        </StyledParagraph>
      );
    }
    if (
      countryCode === HungaryCountryConstant.id ||
      countryCode === HungaryCountryConstant.label
    && phone?.length === 0) {
      return (
        <StyledParagraph>
          {t(
            'Please contact our customer service team at the following email address'
          )}
          &nbsp;
          <StyledLink
            href={`${email.startsWith('http') ? '' : 'mailto:'}${email}`}
            target="_blank"
          >
            {email}
          </StyledLink>
        </StyledParagraph>
      );
    }
    return (
      <StyledParagraph>
        {t('Please contact our support team on')}&nbsp;
        <StyledLink href={`tel:${phone}`} className="contact-detail-span">
          {phone}
        </StyledLink>
        &nbsp;{t('Or email us at')}&nbsp;
        <StyledLink href={`${email.startsWith('http') ? '' : 'mailto:'}${email}`}>
          {email}
        </StyledLink>
        &nbsp;
        {t('For help or queries. Thank you.')}
      </StyledParagraph>
    );
  }, [email, phone, countryCode]);

  return (
    <StyledCard
      slideDirection={slidingDirection}
      onAnimationEnd={handleAnimationEnd}
      data-testid="help-info-container"
    >
      <CloseOutlined
        onClick={handleCloseButtonClick}
        data-testid="help-info-container-close-icon"
      />
      <StyledIcon>
        <IconHelp width="24px" height="24px" color={colors.blue60} />
      </StyledIcon>
      <StyledTextContainer>
        <StyledTitle>{t('Need help title')}</StyledTitle>
        {showContactDetails}
        <StyledHr />
        <StyledLinkCookieSettings
          className="contact-detail-cookie-settings"
          onClick={() => (window as any).Cookiebot.renew()}
        >
          {t('Cookie settings')}
        </StyledLinkCookieSettings>
      </StyledTextContainer>
    </StyledCard>
  );
};

export default HelpContactPopup;
